import { useEffect, useRef, useState } from 'react';
import Wheel from './wheel'; // Assuming Wheel class is in a separate file
import styles from '../../../styles/RemoteControlPAC.module.css';

interface wheelComponentProps {
  up: VoidFunction,
  down: VoidFunction,
  ok: VoidFunction,
  activeButtons: string[],
  wheelShadowColor: string,
  setTouch: any;
}

const WheelComponent = (props: wheelComponentProps) => {
    const { up, down, ok, activeButtons, wheelShadowColor, setTouch } = props; 
    const wheelRef = useRef<Wheel | null>(null);
    const [isWheelClicked, setIsWheelClicked] = useState(true);

    useEffect(() => {
      // Initialize Wheel instance if it doesn't exist
      if (!wheelRef.current) {
        try {
          wheelRef.current = new Wheel(activeButtons);
          
          // Use the modern callback approach instead of direct assignment
          wheelRef.current.onAction('UP', (steps: number) => {
            // Execute the up function for each step
            for (let i = 0; i < steps; i++) {
              up();
            }
          });
          
          wheelRef.current.onAction('DOWN', (steps: number) => {
            // Execute the down function for each step  
            for (let i = 0; i < steps; i++) {
              down();
            }
          });

          // Optional: Set up position change callback for additional feedback
          wheelRef.current.onPositionChange((angle: number, direction: number, speed: number) => {
            // You can add visual feedback, sound effects, or other responses here
            // console.log(`Wheel at ${angle}°, direction: ${direction}, speed: ${speed}`);
          });

          // Optional: Adjust sensitivity if needed
          // wheelRef.current.setAngleThreshold(12); // Default is 15, lower = more sensitive

        } catch (error) {
          console.error('Failed to initialize wheel:', error);
        }
      }

      return () => {
        // Cleanup wheel instance
        if (wheelRef.current) {
          wheelRef.current.destroy();
          wheelRef.current = null;
        }
      };
    }, []); // Empty dependency array - only run once
  
    // Update the Wheel instance's active buttons whenever they change
    useEffect(() => {
      if (wheelRef.current) {
        wheelRef.current.setActiveButtons(activeButtons);
        // Force recalculation of positions in case layout changed
        wheelRef.current.calculatePositions();
      }
    }, [activeButtons]); 

    // Handle window resize to recalculate wheel positions
    useEffect(() => {
      const handleResize = () => {
        if (wheelRef.current) {
          wheelRef.current.calculatePositions();
        }
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);
  
    // Enhanced OK button handlers for mobile compatibility
    const handleOkClick = (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      
      if (activeButtons.includes('OK')) {
        ok();
      }
    };

    const handleOkTouchStart = (event: React.TouchEvent) => {
      event.stopPropagation();
      event.preventDefault();
      
      if (activeButtons.includes('OK')) {
        ok();
      }
    };

    const handleOkTouchEnd = (event: React.TouchEvent) => {
      event.stopPropagation();
      event.preventDefault();
    };

    const handleDocumentClick = (event: MouseEvent) => {
      const wheelContainer = document.getElementById('wheel');
      const Page = document.getElementById("root");
      const menuButton = document.getElementById("menu-button");
      const returnButton = document.getElementById("return-button");
      const astuceButton = document.getElementById("astuce-button");
      
      if (wheelContainer && 
          !wheelContainer.contains(event.target as Node) && 
          Page?.contains(event.target as Node) && 
          !menuButton?.contains(event.target as Node) && 
          !returnButton?.contains(event.target as Node) && 
          !astuceButton?.contains(event.target as Node)) {
        setIsWheelClicked(false);
      }
    };

    useEffect(() => {
      document.addEventListener('click', handleDocumentClick);
      return () => {
        document.removeEventListener('click', handleDocumentClick);
      };
    }, []);

    useEffect(() => {
      if (isWheelClicked) {
        setTouch("none");
      } else {
        setTouch("");
      }
    }, [isWheelClicked, setTouch]);

    // Enhanced wheel handlers to avoid interfering with center button
    const handleWheelClick = (event: React.MouseEvent) => {
      // Check if click is on the inner circle (OK button)
      const target = event.target as HTMLElement;
      const isInnerCircle = target.classList.contains(styles['inner-ellipse']);
      
      if (!isInnerCircle) {
        setIsWheelClicked(true);
      }
    };

    const handleWheelMouseDown = (event: React.MouseEvent) => {
      // Check if mousedown is on the inner circle (OK button)
      const target = event.target as HTMLElement;
      const isInnerCircle = target.classList.contains(styles['inner-ellipse']);
      
      if (!isInnerCircle) {
        event.preventDefault();
        setIsWheelClicked(true);
      }
    };

    const handleWheelTouchStart = (event: React.TouchEvent) => {
      // Check if touch is on the inner circle (OK button)
      const target = event.target as HTMLElement;
      const isInnerCircle = target.classList.contains(styles['inner-ellipse']);
      
      if (!isInnerCircle) {
        setIsWheelClicked(true);
      }
    };

    return (
      <div 
        className={`${styles['nested-ellipses-container']}`}  
        id="wheel" 
        onClick={handleWheelClick}
        onMouseDown={handleWheelMouseDown}
        onTouchStart={handleWheelTouchStart}
        style={{
          boxShadow: `0 0 12px 5px ${wheelShadowColor}`,
          userSelect: 'none',
          touchAction: 'none',
          cursor: isWheelClicked ? 'grabbing' : 'grab'
        }}
      >
        <div className={`${styles['outer-ellipse']}`}></div>
        <div className={`${styles['middle-ellipse']}`}></div>
        <div 
          className={`${styles['inner-ellipse']}`} 
          onClick={handleOkClick}
          onTouchStart={handleOkTouchStart}
          onTouchEnd={handleOkTouchEnd}
          style={{
            cursor: activeButtons.includes('OK') ? 'pointer' : 'default',
            // Override parent's touch-action for this specific element
            touchAction: 'manipulation'
          }}
        ></div>
      </div>
    );
};

export default WheelComponent;